import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Photo } from '@/components/PhotoGrid';
import PhotoDetail from '@/components/PhotoDetail';
import NavBar from '@/components/NavBar';

// Image host base URL (new host stores absolute URLs in config)
const API_BASE_URL = 'https://cdn-bun.stardots.io/swainphoto';
const resolvePhotoUrl = (path: string) => path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;

const PhotoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [nextPhotoId, setNextPhotoId] = useState<string | undefined>();
  const [prevPhotoId, setPrevPhotoId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`/photos.json`);
        const data = await response.json();
        const photos = data.map((p: any): Photo => ({
          id: p.id,
          title: p.title,
          description: p.description,
          url: resolvePhotoUrl(p.full_url),
          thumbnailUrl: resolvePhotoUrl(p.preview_url),
          width: p.width,
          height: p.height,
          category: p.category !== 'uncategorized' ? p.category : undefined,
          createdAt: p.date || new Date().toISOString()
        }));

        const currentIndex = photos.findIndex((p: Photo) => p.id === id);
        if (currentIndex !== -1) {
          setPhoto(photos[currentIndex]);
          setNextPhotoId(photos[currentIndex + 1]?.id);
          setPrevPhotoId(photos[currentIndex - 1]?.id);
        } else {
          setError('Photo not found');
        }
      } catch (error) {
        console.error('Error fetching photo:', error);
        setError('Failed to load photo');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPhoto();
    }
  }, [id]);

  const handleClose = () => {
    navigate('/gallery');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="pt-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="aspect-video bg-gray-200 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !photo) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="pt-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-2xl font-playfair mb-4">
              {error || t('common.photoNotFound')}
            </h1>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              {t('common.backToGallery')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PhotoDetail
      photo={photo}
      nextPhotoId={nextPhotoId}
      prevPhotoId={prevPhotoId}
      onClose={handleClose}
    />
  );
};

export default PhotoDetailPage; 